import React from 'react';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { withLocalePrefix } from 'shared/utils/translation';
import WebIcon from '@grnhse/seedling/lib/birch/icons/web';
import GithubIcon from 'talent_matching/components/icons/github_icon';
import LinkedinIcon from 'talent_matching/components/icons/linkedin_icon';
import DocumentIcon from 'shared/components/documents/document_icon';
import {
  IconButtonExternalLink,
  IconButton,
} from '@grnhse/seedling/lib/birch/components/buttons/icon_buttons';
import { CustomIconTooltip } from '@grnhse/seedling/lib/birch/components/tooltips';
import { Person } from 'talent_matching/types';
import DocumentViewerModal from 'talent_matching/components/shared/document_viewer_modal';
import { Document, Row } from 'talent_matching/types';
import { useUserContext } from 'talent_matching/contexts/user_context';
import { useModal } from '@grnhse/seedling/lib/birch/components/modals';
import FeedbackModal from 'talent_matching/components/modals/leave_feedback_modal';
import RejectModal from 'candidate_profile_redesign/modals/reject/reject_modal';
import { useRejectModal } from '../../../application_review/components/reject_modal_context';

const t = withLocalePrefix('talent_matching');

interface Props {
  application: Person;
  onRejectSuccess: (row: Row, closeModal: () => void) => void;
  isFraud: boolean;
}

const SocialLinks = ({ application, onRejectSuccess, isFraud }: Props) => {
  const { linkedinAddress, githubAddress, websiteAddress, resume } =
    application;
  const [modalDocument, setModalDocument] = React.useState<Document | null>(
    null
  );

  const { paths, canAdvance, canReject } = useUserContext();
  const [showFeedbackModal, openFeedbackModal, closeFeedbackModal] = useModal();
  const {
    rejectIsOpen: rejectModalOpen,
    openReject,
    closeReject: closeRejectModal,
    rejectModalProps,
  } = useRejectModal();

  const openRejectModal = () => {
    openReject();
  };

  const handleWrapperClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <Stack direction="row" spacing={0.5}>
      {linkedinAddress && (
        <CustomIconTooltip label={t('linkedin')} ariaLabel={t('linkedin')}>
          <IconButtonExternalLink
            href={linkedinAddress}
            icon={LinkedinIcon}
            label={t('linkedin')}
            size="sm"
          />
        </CustomIconTooltip>
      )}
      {githubAddress && (
        <CustomIconTooltip label={t('github')} ariaLabel={t('github')}>
          <IconButtonExternalLink
            href={githubAddress}
            icon={GithubIcon}
            label={t('github')}
            size="sm"
          />
        </CustomIconTooltip>
      )}
      {websiteAddress && (
        <CustomIconTooltip label={t('website')} ariaLabel={t('website')}>
          <IconButtonExternalLink
            href={websiteAddress}
            icon={WebIcon}
            label={t('website')}
            size="sm"
          />
        </CustomIconTooltip>
      )}
      {resume?.document && (
        <div onClick={handleWrapperClick} onMouseUp={handleWrapperClick}>
          <CustomIconTooltip label={t('resume')} ariaLabel={t('resume')}>
            <IconButton
              onClick={() => setModalDocument(resume.document)}
              icon={() => (
                <DocumentIcon extension={resume.document.extension} />
              )}
              label={t('resume')}
              size="sm"
            />
          </CustomIconTooltip>
          {modalDocument && (
            <DocumentViewerModal
              document={modalDocument}
              onClose={() => {
                setModalDocument(null);
              }}
              openFeedbackModal={openFeedbackModal}
              openRejectModal={openRejectModal}
              application={application}
              canAdvance={canAdvance}
              canReject={canReject}
            />
          )}

          {application && (
            <>
              <FeedbackModal
                isOpen={showFeedbackModal}
                closeModal={closeFeedbackModal}
                application={application}
                hiringPlanTagsPath={paths.hiringPlanTagsPath}
              />
              <RejectModal
                bulkMode={false}
                closeRejectModal={closeRejectModal}
                isRejectModalOpen={rejectModalOpen}
                getRejectModalPath={application.rejectModalPath}
                candidateName={application.fullName}
                changeApplicationStatePath={application.rejectPath}
                onRejectSuccess={() => {
                  onRejectSuccess(application, closeRejectModal);
                  setModalDocument(null);
                }}
                {...rejectModalProps}
              />
            </>
          )}
        </div>
      )}
    </Stack>
  );
};

export default SocialLinks;
